import { watch } from 'vue'
import { useHead } from 'nuxt/app'

import { Theme, useTheme } from '@common/theme-layer/composables/useTheme'

import { useLocationStore } from '../store/location.store'
import { useVibeHost } from './useVibeHost'
import type { AiBuilderTheme } from '../types/location'

const GOOGLE_FONTS_STYLESHEET_PREFIX = 'https://fonts.googleapis.com/'
// Both values are written into a <style> block, so anything outside these shapes is dropped.
const FONT_FAMILY_NAME = /^[A-Za-z0-9 -]+$/
const CSS_LENGTH = /^\d*\.?\d+(px|rem|em)$/

/**
 * ClientPortal's Tailwind radius steps follow the app's radius the way the app's own
 * (shadcn) classes do. Cards (CPCardv2, post cards, anything on rounded-md/lg/xl and their
 * top/bottom variants), CPButton and every HighRise input follow it. Other HighRise
 * components (modals, dropdowns, tags, checkboxes, HLCard) keep their own radius.
 */
const RADIUS_STEPS = {
  md: 'calc(var(--cp-radius) - 2px)',
  lg: 'var(--cp-radius)',
  xl: 'calc(var(--cp-radius) + 4px)',
}

/** The AI App Builder app's theme on its own host (vibe host mode); undefined everywhere else. */
export const useVibeAppTheme = (): AiBuilderTheme | undefined =>
  useVibeHost() ? useLocationStore().locationData?.aiBuilderTheme : undefined

const isGoogleFontsStylesheet = (url: unknown): url is string => typeof url === 'string' && url.startsWith(GOOGLE_FONTS_STYLESHEET_PREFIX)

const fontStack = (name?: string): string | undefined =>
  name && FONT_FAMILY_NAME.test(name) ? `"${name}", system-ui, sans-serif` : undefined

const radiusCss = (radius: string): string =>
  `:root{--cp-radius:${radius}}` +
  Object.entries(RADIUS_STEPS)
    .map(
      ([step, value]) =>
        `:root .rounded-${step}{border-radius:${value}}` +
        `:root .rounded-t-${step}{border-top-left-radius:${value};border-top-right-radius:${value}}` +
        `:root .rounded-b-${step}{border-bottom-left-radius:${value};border-bottom-right-radius:${value}}`
    )
    .join('') +
  // HighRise writes --n-border-radius inline, so only !important reaches it (same as CPButton/CPInput).
  `.hr-button.cp-button,.hr-input{--n-border-radius:${RADIUS_STEPS.md}!important}`

/** The app's fonts and radius as CSS; colours go through the theme engine instead. */
const vibeAppThemeCss = ({ fonts, radius }: AiBuilderTheme): string => {
  const body = fontStack(fonts?.body)
  const heading = fontStack(fonts?.heading) ?? body

  return [
    // `:root:root` beats HighRise's own `:root{--hr-font-family-base}` whatever the stylesheet order.
    body && `:root:root{--cp-font-family-body:${body};--hr-font-family-base:${body}}`,
    heading && `:root{--cp-font-family-heading:${heading}}:root :is(h1,h2,h3,h4,h5,h6){font-family:var(--cp-font-family-heading)}`,
    radius && CSS_LENGTH.test(radius) && radiusCss(radius),
  ]
    .filter(Boolean)
    .join('')
}

/**
 * Makes ClientPortal look like the vibe app it serves `/communities` for: the app's colours,
 * fonts and corner radius. Mount once in the root shell with `useVibeAppTheme()`; a no-op
 * without a theme (any portal outside vibe host mode, or an app without one).
 *
 * The colours are locked in the theme engine, so the app wins over the portal's branding
 * (`usePortalTheme`) and a community group's own theme (`useCommunityTheme`). Runs during
 * setup, so SSR paints the first frame in the app's theme.
 */
export const useApplyVibeAppTheme = (theme: AiBuilderTheme | undefined): void => {
  if (!theme) return

  const { currentTheme, setBrandTokens } = useTheme()

  watch(
    currentTheme,
    mode => {
      const colors = mode === Theme.DARK && theme.darkColors ? theme.darkColors : theme.colors
      // Without dark colours, dark mode keeps ClientPortal's dark chrome and takes only the app's accents.
      setBrandTokens(colors, { includeChromeInDark: Boolean(theme.darkColors), lock: true })
    },
    { immediate: true }
  )

  const css = vibeAppThemeCss(theme)
  useHead({
    link: (theme.fonts?.stylesheets ?? []).filter(isGoogleFontsStylesheet).map(href => ({ rel: 'stylesheet', href })),
    // The rail's icons read --cp-platform-primary, which applyBranding writes inline from the portal's brand colour.
    style: [{ key: 'cp-vibe-app-theme', innerHTML: `:root:root{--cp-platform-primary:var(--cp-brand-primary)!important}${css}` }],
  })
}
